import { makeNamespacedLog } from '@sb/log';

import type { CaptureStorageReport } from './captureStoragePressure';

const log = makeNamespacedLog('notifyCaptureStorage');

/**
 * The part of the feathers client this needs.
 *
 * Structural, so this file does not pull `@feathersjs/feathers` into the
 * runner, and narrow, so it says exactly what it does: read a skill, write a
 * notification.
 */
export interface CaptureStorageFeathersApp {
  service(name: string): {
    get(id: string): Promise<unknown>;
    create(data: unknown): Promise<unknown>;
  };
}

/** Falls back to the id, which still identifies the directory in the message. */
async function resolveSkillName(
  app: CaptureStorageFeathersApp,
  skillId: string,
): Promise<string> {
  const skill = await app
    .service('skills')
    .get(skillId)
    .catch(() => null);

  const name = (skill as { name?: unknown } | null)?.name;

  return typeof name === 'string' && name ? name : skillId;
}

/**
 * Builds the callback `captureFrameToStorage` uses to raise a storage warning.
 *
 * Returns `undefined` where there is no feathers client — vizbot and the test
 * harnesses — so the capture path skips the work rather than each caller having
 * to check.
 *
 * The returned function does not return a promise and is never awaited. A
 * capture that saved everything it was asked to must not be held up, or
 * reported as failed, because a notification could not be delivered; the work
 * is started and its failure goes to the log.
 */
export function makeCaptureStorageNotifier(context: {
  robotId: string;
  feathersApp?: CaptureStorageFeathersApp;
}): ((report: CaptureStorageReport) => void) | undefined {
  const { robotId, feathersApp } = context;

  if (!feathersApp) return undefined;

  return (report) => {
    // Deliberately not returned and not awaited; see above.
    (async () => {
      // The disk floor is the robot's, so there is no skill to name and no
      // lookup to make. A stand-in name would put a skill in front of an
      // operator whose captures are inside their budget.
      const skillName =
        report.level === 'diskFloor'
          ? undefined
          : await (report.skillId
              ? resolveSkillName(feathersApp, report.skillId)
              : Promise.resolve('Captures with no skill'));

      await feathersApp.service('notifications').create({
        robotID: robotId,
        kind: 'captureStorage',
        routineID: report.routineId,
        skillID: report.skillId,
        skillName,
        storageLevel: report.level,
        storageUsedBytes: report.usedBytes,
        storageLimitBytes: report.limitBytes,
      });
    })().catch((error: unknown) => {
      log.warn('notify', 'Could not report capture storage pressure', {
        directory: report.directory,
        level: report.level,
        error: error instanceof Error ? error.message : String(error),
      });
    });
  };
}
